import type { Api } from '@studio/design-system/dialog';
import { ensureNotNil } from '@studio/js-utils';
import { useLatestFn, useLazyRef, χref } from '@studio/react-utils/hooks';
import { useContext } from 'react';
import type { RefObject } from 'react';
import { Context } from './context.js';
import { useStore } from './store/hooks.js';
import type { Wiring } from './types.js';
import { update, audioEgress } from '#~/audio/commands/index.js';
import { useRemove } from '#~/device/components/hooks.js';
import { raiseError } from '#~/notifications/index.js';
import { useAuth0 } from '#~/auth/index.js';

/** @internal */
export const useWiring = (): Wiring => ensureNotNil(useContext(Context));

/** @internal */
export const useWorkflow = (apiRef: RefObject<Api | null>): Wiring => {
  const candidate = useStore((µ) => µ.candidate);
  const remove = useRemove({ type: 'audio' });
  const { getAccessTokenSilently } = useAuth0();

  const handleClose = useLatestFn(() => {
    χref(apiRef)?.close(true);
  });

  const handleCommit = useLatestFn(async () => {
    const token = await getAccessTokenSilently();

    try {
      // Update the audio device.
      await update(candidate.id, candidate, token);

      handleClose();
    } catch (error) {
      raiseError(<Error>error, { actionable: true });
    }
  });

  const handleRemove = useLatestFn(() => {
    const { id, name } = candidate;

    remove({ id, name }, () => {
      handleClose();
    });
  });

  // Temporary sends the source name as the sink name, for final implementation,
  // the user will be able to set the sink name or use the source name as default.
  const handleStartEgress = useLatestFn(() => {
    candidate.name;
    audioEgress(candidate.id, candidate.name, true).catch((error) =>
      raiseError(error, { actionable: true })
    );
  });

  const handleStopEgress = useLatestFn(() => {
    audioEgress(candidate.id, candidate.name, false).catch((error) =>
      raiseError(error, { actionable: true })
    );
  });

  return useLazyRef(() => ({
    handleCancel: handleClose,
    handleClose,
    handleCommit,
    handleRemove,
    handleStartEgress,
    handleStopEgress
  })).current;
};
